import React, { useState, useEffect } from "react";
import {
  useEditor,
  EditorContent,
  ReactNodeViewRenderer,
  NodeViewWrapper,
} from "@tiptap/react";
import { Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Paragraph from "@tiptap/extension-paragraph";
import { useQuery } from "@tanstack/react-query";
import { Image as ImagePlaceholderIcon, X } from "lucide-react";
import { fileService } from "@/services/breeze/FileService";

function fileIdFromCid(src: string): number | null {
  const m = /^cid:(\d+)-/.exec(src);
  return m ? Number(m[1]) : null;
}

interface ImageNodeViewProps {
  node: { attrs: Record<string, any> };
  editor: any;
  getPos: () => number;
  selected?: boolean;
}

const ImagePlaceholderView: React.FC<ImageNodeViewProps> = ({
  node,
  editor,
  getPos,
  selected,
}) => {
  const src = node.attrs.src ?? "";
  const alt = node.attrs.alt ?? "image";
  const fileId = node.attrs["data-anythink-file-id"]
    ? Number(node.attrs["data-anythink-file-id"])
    : fileIdFromCid(src);
  const isCid = typeof src === "string" && src.startsWith("cid:");

  const { data: signedUrl } = useQuery({
    queryKey: ["file-signed-url", fileId],
    queryFn: () => fileService.getFileSignedUrl(fileId!),
    enabled: isCid && !!fileId,
    staleTime: 4 * 60 * 1000,
  });

  const displaySrc = isCid ? signedUrl : src;
  const imgRef = React.useRef<HTMLImageElement>(null);
  const [hover, setHover] = useState(false);

  const deleteSelf = () => {
    const pos = getPos();
    editor.chain().focus().setNodeSelection(pos).deleteSelection().run();
  };

  const wrapperStyle: React.CSSProperties = {
    margin: "8px 0",
    position: "relative",
    display: "inline-block",
    outline: selected ? "2px solid var(--accent-9)" : "none",
    outlineOffset: 2,
    borderRadius: 4,
  };

  return (
    <NodeViewWrapper
      as="div"
      style={wrapperStyle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {displaySrc ? (
        <img
          ref={imgRef}
          src={displaySrc}
          alt={alt}
          draggable="true"
          data-drag-handle
          className="anythink-editor-image"
          style={{
            maxWidth: "100%",
            height: "auto",
            display: "block",
            cursor: "grab",
            ...(node.attrs.style ? parseStyleString(node.attrs.style) : {}),
          }}
        />
      ) : (
        <div
          contentEditable={false}
          draggable="true"
          data-drag-handle
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            border: "1px dashed var(--gray-7)",
            background: "var(--gray-2)",
            borderRadius: 6,
            color: "var(--gray-11)",
            fontSize: 12,
            userSelect: "none",
            cursor: "grab",
          }}
        >
          <ImagePlaceholderIcon size={16} />
          <span>{alt || "Inline image"}</span>
        </div>
      )}
      {(hover || selected) && (
        <button
          type="button"
          contentEditable={false}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            deleteSelf();
          }}
          title="Remove image"
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 26,
            height: 26,
            borderRadius: 13,
            border: "none",
            background: "rgba(0,0,0,0.65)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
          }}
        >
          <X size={14} />
        </button>
      )}
    </NodeViewWrapper>
  );
};

const inFlightUploads = new Set<string>();
function uploadOnce(file: globalThis.File, run: () => void | Promise<void>) {
  const key = `${file.name}:${file.size}:${file.lastModified}`;
  if (inFlightUploads.has(key)) return;
  inFlightUploads.add(key);
  Promise.resolve(run()).finally(() => {
    setTimeout(() => inFlightUploads.delete(key), 2000);
  });
}

async function insertUploadedImage(
  view: any,
  pos: number,
  file: globalThis.File,
) {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const uploaded = await fileService.create(formData);
    const safeName = (
      uploaded.original_file_name || `file-${uploaded.id}`
    ).replace(/[^A-Za-z0-9._-]/g, "_");
    const cid = `${uploaded.id}-${safeName}`;
    const node = view.state.schema.nodes.image;
    if (!node) return;
    const tr = view.state.tr.insert(
      pos,
      node.create({
        src: `cid:${cid}`,
        alt: safeName,
        style: "max-width:100%; height:auto;",
        "data-anythink-file-id": String(uploaded.id),
      }),
    );
    view.dispatch(tr);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Image upload failed", err);
  }
}

function parseStyleString(s: string): React.CSSProperties {
  const out: Record<string, string> = {};
  s.split(";").forEach((kv) => {
    const [k, v] = kv.split(":").map((p) => p?.trim());
    if (!k || !v) return;
    const camel = k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    out[camel] = v;
  });
  return out as React.CSSProperties;
}

const ImageNode = Node.create({
  name: "image",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      style: { default: null },
      width: { default: null },
      height: { default: null },
      "data-anythink-file-id": {
        default: null,
        parseHTML: (el) => el.getAttribute("data-anythink-file-id"),
        renderHTML: (attrs) => {
          if (!attrs["data-anythink-file-id"]) return {};
          return { "data-anythink-file-id": attrs["data-anythink-file-id"] };
        },
      },
    };
  },
  parseHTML() {
    return [{ tag: "img[src]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImagePlaceholderView as any);
  },
});
import { Markdown } from "tiptap-markdown";
import { SearchAndReplace } from "@sereneinserenade/tiptap-search-and-replace";
import {
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Code,
  Undo2,
  Redo2,
  Search,
  Replace,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import {
  Box,
  Flex,
  Button,
  TextField,
  Separator,
  Text,
} from "@radix-ui/themes";
import { cn } from "@/utils/cn";
import "./RichTextEditor.css";

interface RichTextEditorProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly rows?: number;
  readonly enableMarkdown?: boolean;
  readonly className?: string;
  readonly onEditorReady?: (editor: import("@tiptap/react").Editor) => void;
}

export function RichTextEditor({
  value,
  onChange,
  disabled = false,
  placeholder = "Start writing...",
  rows = 6,
  enableMarkdown = true,
  className,
  onEditorReady,
}: RichTextEditorProps) {
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Exclude default Paragraph so we can use our extended version
        paragraph: false,
      }),
      // Extended Paragraph with style attribute support
      Paragraph.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            style: {
              default: null,
              parseHTML: (element) => element.getAttribute("style"),
              renderHTML: (attributes) => {
                if (!attributes.style) {
                  return {};
                }
                return { style: attributes.style };
              },
            },
          };
        },
      }),
      // Extended Link with class and style attribute support
      Link.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            class: {
              default: null,
              parseHTML: (element) => element.getAttribute("class"),
              renderHTML: (attributes) => {
                if (!attributes.class) {
                  return {};
                }
                return { class: attributes.class };
              },
            },
            style: {
              default: null,
              parseHTML: (element) => element.getAttribute("style"),
              renderHTML: (attributes) => {
                if (!attributes.style) {
                  return {};
                }
                return { style: attributes.style };
              },
            },
          };
        },
      }).configure({
        openOnClick: false,
        HTMLAttributes: {
          class: null,
        },
      }),
      ...(enableMarkdown
        ? [
            Markdown.configure({
              html: false,
              transformPastedText: true,
              transformCopiedText: true,
            }),
          ]
        : []),
      SearchAndReplace.configure({
        searchResultClass: "search-result",
        disableRegex: false,
      }),
      ImageNode,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const content = enableMarkdown
        ? (editor.storage.markdown?.getMarkdown() ?? editor.getHTML())
        : editor.getHTML();
      onChange(content);
    },
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none focus:outline-none",
          "min-h-[120px] p-3 text-sm",
          className,
        ),
        style: `min-height: ${rows * 24}px`,
      },
      handleDOMEvents: {
        dragstart(view, event) {
          const target = event.target as HTMLElement | null;
          if (target?.closest(".anythink-editor-image")) {
            view.dom.setAttribute("data-img-dragging", "true");
            const clear = () => {
              view.dom.removeAttribute("data-img-dragging");
              window.removeEventListener("dragend", clear);
              window.removeEventListener("drop", clear);
            };
            window.addEventListener("dragend", clear);
            window.addEventListener("drop", clear);
          }
          return false;
        },
        dragend(view) {
          view.dom.removeAttribute("data-img-dragging");
          return false;
        },
      },
      handleDrop(view, event, _slice, moved) {
        view.dom.removeAttribute("data-img-dragging");
        if (moved) return false;
        const files = Array.from(event.dataTransfer?.files ?? []).filter((f) =>
          f.type.startsWith("image/"),
        );
        if (files.length === 0) {
          const types = event.dataTransfer
            ? Array.from(event.dataTransfer.types)
            : [];
          const looksLikeImage = types.some(
            (t) => t === "text/uri-list" || t === "text/html",
          );
          if (looksLikeImage) {
            event.preventDefault();
            event.stopPropagation();
            return true;
          }
          return false;
        }
        event.preventDefault();
        event.stopPropagation();
        const coords = view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        });
        const pos = coords?.pos ?? view.state.selection.head;
        files.forEach((file) =>
          uploadOnce(file, () => insertUploadedImage(view, pos, file)),
        );
        return true;
      },
      handlePaste(view, event) {
        const items = Array.from(event.clipboardData?.items ?? []).filter((i) =>
          i.type.startsWith("image/"),
        );
        if (items.length === 0) return false;
        event.preventDefault();
        event.stopPropagation();
        const pos = view.state.selection.head;
        items.forEach((item) => {
          const file = item.getAsFile();
          if (file)
            uploadOnce(file, () => insertUploadedImage(view, pos, file));
        });
        return true;
      },
    },
  });

  // Update content when value changes externally
  useEffect(() => {
    if (
      editor &&
      value !==
        (enableMarkdown
          ? (editor.storage.markdown?.getMarkdown() ?? editor.getHTML())
          : editor.getHTML())
    ) {
      editor.commands.setContent(value);
    }
  }, [value, editor, enableMarkdown]);

  // Expose the editor instance to parent once available so external toolbars
  // can dispatch insertions at the current cursor.
  useEffect(() => {
    if (editor && onEditorReady) onEditorReady(editor);
  }, [editor, onEditorReady]);

  // Handle search functionality
  useEffect(() => {
    if (editor && searchTerm) {
      editor.commands.setSearchTerm(searchTerm);
    }
  }, [editor, searchTerm]);

  useEffect(() => {
    if (editor && replaceTerm) {
      editor.commands.setReplaceTerm(replaceTerm);
    }
  }, [editor, replaceTerm]);

  const toggleSearch = () => {
    if (!editor) return;
    setShowSearch(!showSearch);
    if (!showSearch) {
      setSearchTerm("");
      setReplaceTerm("");
      editor.commands.setSearchTerm("");
      editor.commands.setReplaceTerm("");
    }
  };

  if (!editor) {
    return (
      <Box
        style={{
          height: `${rows * 24 + 100}px`,
          minHeight: "180px",
        }}
        className="animate-pulse bg-gray-2 rounded-md"
      />
    );
  }

  return (
    <Box
      className={cn(
        "border border-gray-6 rounded-md overflow-hidden",
        className,
      )}
    >
      {/* Toolbar */}
      <Flex
        align="center"
        justify="between"
        p="2"
        className="bg-gray-2 border-b border-gray-6"
        gap="2"
      >
        <Flex align="center" gap="1">
          {/* Text Formatting */}
          <Button
            size="1"
            variant="ghost"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive("bold") ? "bg-accent-3" : ""}
            disabled={disabled}
            type="button"
          >
            <Bold size={14} />
          </Button>
          <Button
            size="1"
            variant="ghost"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive("italic") ? "bg-accent-3" : ""}
            disabled={disabled}
            type="button"
          >
            <Italic size={14} />
          </Button>
          <Button
            size="1"
            variant="ghost"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={editor.isActive("strike") ? "bg-accent-3" : ""}
            disabled={disabled}
            type="button"
          >
            <Strikethrough size={14} />
          </Button>

          <Separator orientation="vertical" size="1" />

          {/* Headings */}
          <Button
            size="1"
            variant="ghost"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            className={
              editor.isActive("heading", { level: 1 }) ? "bg-accent-3" : ""
            }
            disabled={disabled}
            type="button"
          >
            <Text size="1" weight="bold">
              H1
            </Text>
          </Button>
          <Button
            size="1"
            variant="ghost"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            className={
              editor.isActive("heading", { level: 2 }) ? "bg-accent-3" : ""
            }
            disabled={disabled}
            type="button"
          >
            <Text size="1" weight="bold">
              H2
            </Text>
          </Button>
          <Button
            size="1"
            variant="ghost"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            className={
              editor.isActive("heading", { level: 3 }) ? "bg-accent-3" : ""
            }
            disabled={disabled}
            type="button"
          >
            <Text size="1" weight="bold">
              H3
            </Text>
          </Button>

          <Separator orientation="vertical" size="1" />

          {/* Lists and Blocks */}
          <Button
            size="1"
            variant="ghost"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive("bulletList") ? "bg-accent-3" : ""}
            disabled={disabled}
            type="button"
          >
            <List size={14} />
          </Button>
          <Button
            size="1"
            variant="ghost"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive("orderedList") ? "bg-accent-3" : ""}
            disabled={disabled}
            type="button"
          >
            <ListOrdered size={14} />
          </Button>
          <Button
            size="1"
            variant="ghost"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={editor.isActive("blockquote") ? "bg-accent-3" : ""}
            disabled={disabled}
            type="button"
          >
            <Quote size={14} />
          </Button>
          <Button
            size="1"
            variant="ghost"
            onClick={() => editor.chain().focus().toggleCode().run()}
            className={editor.isActive("code") ? "bg-accent-3" : ""}
            disabled={disabled}
            type="button"
          >
            <Code size={14} />
          </Button>

          <Separator orientation="vertical" size="1" />

          {/* Undo/Redo */}
          <Button
            size="1"
            variant="ghost"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo() || disabled}
            type="button"
          >
            <Undo2 size={14} />
          </Button>
          <Button
            size="1"
            variant="ghost"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo() || disabled}
            type="button"
          >
            <Redo2 size={14} />
          </Button>
        </Flex>

        {/* Search Toggle */}
        <Button
          size="1"
          variant="ghost"
          onClick={toggleSearch}
          className={showSearch ? "bg-accent-3" : ""}
          disabled={disabled}
          type="button"
        >
          <Search size={14} />
        </Button>
      </Flex>

      {/* Search Panel */}
      {showSearch && (
        <Box p="2" className="bg-gray-1 border-b border-gray-6">
          <Flex align="center" gap="2">
            <TextField.Root
              size="1"
              placeholder="Find..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ minWidth: "120px" }}
            />
            <TextField.Root
              size="1"
              placeholder="Replace..."
              value={replaceTerm}
              onChange={(e) => setReplaceTerm(e.target.value)}
              style={{ minWidth: "120px" }}
            />
            <Button
              size="1"
              variant="soft"
              onClick={() => editor.commands.previousSearchResult()}
              disabled={!searchTerm}
              type="button"
            >
              <ChevronUp size={12} />
            </Button>
            <Button
              size="1"
              variant="soft"
              onClick={() => editor.commands.nextSearchResult()}
              disabled={!searchTerm}
              type="button"
            >
              <ChevronDown size={12} />
            </Button>
            <Button
              size="1"
              variant="soft"
              onClick={() => editor.commands.replace()}
              disabled={!searchTerm || !replaceTerm}
              type="button"
            >
              <Replace size={12} />
            </Button>
            <Button
              size="1"
              variant="soft"
              onClick={() => editor.commands.replaceAll()}
              disabled={!searchTerm || !replaceTerm}
              type="button"
            >
              <Text size="1">All</Text>
            </Button>
          </Flex>
        </Box>
      )}

      {/* Editor Content */}
      <Box className="relative">
        <EditorContent editor={editor} className="rich-text-editor-content" />
        {!value && (
          <Text
            size="2"
            color="gray"
            className="absolute top-3 left-3 pointer-events-none"
          >
            {placeholder}
          </Text>
        )}
      </Box>
    </Box>
  );
}
